import { FC, ReactNode, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  IconDashboard,
  IconInfoCircle,
  IconMenu,
  IconSearch,
  IconStops,
  IconUsers,
  IconWorkOrder,
  IconWorkOrderCreate,
} from '../../icons';
import Tooltip from '../Tooltip';
import IconButton from '@/components/core/IconButton';
import UserMenu from './UserMenu';
import MainMenu from './MainMenu';
import useSearch from '../../hooks/useSearch';
import { useTopBar } from '../../store/topbar';
import useGuideStore from '../../store/guide';
import { cn } from '../../helpers/classnames';
import NavButton from './NavButton';
import { Routes } from '../../hooks/useRoutes';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import Permissions from '../auth/Permissions';
import useEditWorkOrderDialogStore from '../../store/editWorkOrderDialog';
import useUserStore from '../../store/user';
import { getTenantFeatures } from '../../helpers/tenant_features';
import useConfigStore from '../../store/config';
import { allAuthUsersUrl } from '../../helpers/allauth_urls';
import { useLogout } from '../../hooks/useLogout';

const messages = defineMessages({
  userManagement: {
    id: 'top_bar.user_management',
    defaultMessage: 'User management',
  },
});

const ExternalIconLink: FC<{
  href: string;
  tooltip: ReactNode;
  icon: ReactNode;
  ariaLabel: string;
}> = ({ href, tooltip, icon, ariaLabel }) => (
  <Tooltip label={tooltip}>
    <a
      href={href}
      aria-label={ariaLabel}
      target="_blank"
      rel="noreferrer"
      tabIndex={-1}
    >
      <IconButton
        size="lg"
        variant="ghost"
        icon={icon}
        round
        className="hover:bg-white/50"
      />
    </a>
  </Tooltip>
);

const TopBar: FC = () => {
  const intl = useIntl();
  const { get: getLocalStorage } = useLocalStorage();
  const { doSearch } = useSearch();
  const { title, subtitle, color, guideSubjectId } = useTopBar();
  const { openGuide } = useGuideStore();
  const { open: openEditWorkOrderDialog } = useEditWorkOrderDialogStore();
  const tenant = useUserStore((state) => state.tenant);
  const { dashboardEnabled } = getTenantFeatures(tenant);
  const userManagementUrl = useConfigStore(
    (state) => state.allauth.userManagementUrl,
  );
  const userManagementHref = allAuthUsersUrl(userManagementUrl);
  const userManagementLabel = intl.formatMessage(messages.userManagement);
  const performLogout = useLogout();

  const textColor = useMemo(() => {
    return color === 'workorder'
      ? 'text-contrast-workorder'
      : 'text-contrast-primary';
  }, [color]);

  return (
    <>
      <header
        className={cn(
          'relative z-10 max-h-12 min-h-[48px]',
          color === 'workorder' ? 'bg-workorder-500' : 'bg-primary-500',
          textColor,
        )}
      >
        <div className="absolute inset-0 flex items-center justify-center gap-1 py-1">
          <h1
            className={cn(
              'whitespace-nowrap font-sans text-xl font-medium',
              textColor,
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <h2
              className={cn('whitespace-nowrap text-xl opacity-90', textColor)}
            >
              {subtitle}
            </h2>
          )}
        </div>
        <div className="absolute inset-y-0 left-4 flex items-center gap-4 py-1">
          <MainMenu
            trigger={
              <IconButton
                size="lg"
                variant="ghost"
                aria-label={intl.formatMessage({
                  id: 'top_bar.main_menu',
                  defaultMessage: 'Main menu',
                })}
                icon={<IconMenu className={textColor} />}
                className="hover:bg-white/50"
                round
              />
            }
          />
          {dashboardEnabled && (
            <NavButton
              tooltip={
                <FormattedMessage
                  id="top_bar.dashboard"
                  defaultMessage="Overview"
                />
              }
              to={Routes.dashboard}
              icon={<IconDashboard className={textColor} />}
            />
          )}
          <NavButton
            tooltip={
              <FormattedMessage
                id="top_bar.workorders"
                defaultMessage="Work orders"
              />
            }
            to={Routes.workOrderOverviewState}
            params={{
              state: getLocalStorage(
                'WorkOrderOverview.stateFilter',
                'created',
              ),
            }}
            icon={<IconWorkOrder className={textColor} />}
          />
          <NavButton
            tooltip={
              <FormattedMessage id="top_bar.stops" defaultMessage="Stops" />
            }
            to={Routes.stationOverview}
            icon={<IconStops className={textColor} />}
          />
          {userManagementHref && (
            <Permissions oneOf={['role:admin', 'role:contractor-admin']}>
              <ExternalIconLink
                tooltip={userManagementLabel}
                ariaLabel={userManagementLabel}
                href={userManagementHref}
                icon={<IconUsers className={cn('size-6', textColor)} />}
              />
            </Permissions>
          )}
          <Tooltip
            label={
              <FormattedMessage id="top_bar.search" defaultMessage="Search" />
            }
          >
            <IconButton
              size="lg"
              variant="ghost"
              icon={<IconSearch className={textColor} />}
              onClick={() => doSearch('')}
              className="hover:bg-white/50"
              round
            />
          </Tooltip>
          <Permissions has="workorders:create">
            <Tooltip
              label={
                <FormattedMessage
                  id="top_bar.create_work_order"
                  defaultMessage="Create work order"
                />
              }
            >
              <IconButton
                size="lg"
                variant="ghost"
                icon={<IconWorkOrderCreate className={textColor} />}
                onClick={() => openEditWorkOrderDialog()}
                className="hover:bg-white/50"
                round
              />
            </Tooltip>
          </Permissions>
        </div>
        <div className="absolute inset-y-0 right-4 flex items-center gap-4 py-1">
          <UserMenu onLogout={performLogout} textColor={textColor} />
          <Tooltip
            label={
              <FormattedMessage id="top_bar.guide" defaultMessage="Guide" />
            }
          >
            <IconButton
              size="lg"
              variant="ghost"
              icon={<IconInfoCircle className={textColor} />}
              onClick={() => openGuide(guideSubjectId)}
              className="hover:bg-white/50"
              round
            />
          </Tooltip>
        </div>
      </header>
    </>
  );
};

export default TopBar;
